/**
 * Branded PLEKO loader — animated pot with steam, floating ingredients,
 * glassmorphism backdrop, and rotating cooking messages.
 */
import { useState, useEffect } from 'react';

const cookingMessages = [
  'Preparando tus datos...',
  'Calentando la sartén...',
  'Removiendo los números...',
  'Añadiendo un toque de sal...',
  'Dejando reposar la masa...',
  'Emplatando resultados...',
  'Cortando en juliana...',
  'Flameando las cifras...',
];

interface PlekoLoaderProps {
  message?: string;
  size?: 'sm' | 'md' | 'lg';
  rotate?: boolean;
}

export default function PlekoLoader({ message, size = 'md', rotate = true }: PlekoLoaderProps) {
  const [msgIndex, setMsgIndex] = useState(() => Math.floor(Math.random() * cookingMessages.length));
  const [fade, setFade] = useState(true);

  useEffect(() => {
    if (!rotate || message) return;
    const interval = setInterval(() => {
      setFade(false);
      setTimeout(() => {
        setMsgIndex((i) => (i + 1) % cookingMessages.length);
        setFade(true);
      }, 300);
    }, 2800);
    return () => clearInterval(interval);
  }, [rotate, message]);

  const displayMsg = message || cookingMessages[msgIndex];

  const sizes = {
    sm: { container: 'w-20 h-20', text: 'text-xs', dot: 'w-1 h-1' },
    md: { container: 'w-28 h-28', text: 'text-sm', dot: 'w-1.5 h-1.5' },
    lg: { container: 'w-36 h-36', text: 'text-base', dot: 'w-2 h-2' },
  };

  return (
    <div className="flex flex-col items-center justify-center py-16 select-none">
      {/* Glow backdrop */}
      <div className="relative">
        <div className="absolute inset-0 blur-3xl opacity-20 bg-blue-500 rounded-full scale-150" />

        <div className={`${sizes[size].container} relative`}>
          <svg viewBox="0 0 100 100" fill="none" className="w-full h-full drop-shadow-lg">
            {/* Floating ingredient particles */}
            <circle cx="20" cy="25" r="2" fill="#F59E0B" opacity="0.7">
              <animate attributeName="cy" values="25;15;25" dur="3s" repeatCount="indefinite" />
              <animate attributeName="cx" values="20;24;20" dur="3s" repeatCount="indefinite" />
              <animate attributeName="opacity" values="0.7;0.2;0.7" dur="3s" repeatCount="indefinite" />
            </circle>
            <circle cx="78" cy="22" r="1.5" fill="#10B981" opacity="0.6">
              <animate attributeName="cy" values="22;12;22" dur="2.5s" repeatCount="indefinite" begin="0.5s" />
              <animate attributeName="cx" values="78;74;78" dur="2.5s" repeatCount="indefinite" begin="0.5s" />
              <animate attributeName="opacity" values="0.6;0.1;0.6" dur="2.5s" repeatCount="indefinite" begin="0.5s" />
            </circle>
            <circle cx="50" cy="18" r="1.8" fill="#EF4444" opacity="0.5">
              <animate attributeName="cy" values="18;6;18" dur="3.2s" repeatCount="indefinite" begin="1s" />
              <animate attributeName="opacity" values="0.5;0;0.5" dur="3.2s" repeatCount="indefinite" begin="1s" />
            </circle>

            {/* Steam wisps — organic curved paths */}
            <path d="M 35 32 Q 33 24 36 16" stroke="#93C5FD" strokeWidth="2" strokeLinecap="round" fill="none" opacity="0.5">
              <animate attributeName="d" values="M 35 32 Q 33 24 36 16;M 35 32 Q 37 22 34 14;M 35 32 Q 33 24 36 16" dur="2.4s" repeatCount="indefinite" />
              <animate attributeName="opacity" values="0.5;0.15;0.5" dur="2.4s" repeatCount="indefinite" />
            </path>
            <path d="M 50 30 Q 48 20 51 10" stroke="#93C5FD" strokeWidth="2.5" strokeLinecap="round" fill="none" opacity="0.6">
              <animate attributeName="d" values="M 50 30 Q 48 20 51 10;M 50 30 Q 53 18 49 8;M 50 30 Q 48 20 51 10" dur="2.8s" repeatCount="indefinite" begin="0.4s" />
              <animate attributeName="opacity" values="0.6;0.1;0.6" dur="2.8s" repeatCount="indefinite" begin="0.4s" />
            </path>
            <path d="M 65 32 Q 67 22 64 14" stroke="#93C5FD" strokeWidth="1.5" strokeLinecap="round" fill="none" opacity="0.4">
              <animate attributeName="d" values="M 65 32 Q 67 22 64 14;M 65 32 Q 63 24 66 16;M 65 32 Q 67 22 64 14" dur="2s" repeatCount="indefinite" begin="0.8s" />
              <animate attributeName="opacity" values="0.4;0.05;0.4" dur="2s" repeatCount="indefinite" begin="0.8s" />
            </path>

            {/* Pot lid — bouncing */}
            <g>
              <animateTransform
                attributeName="transform"
                type="translate"
                values="0 0;0 -3;0 0"
                dur="0.6s"
                repeatCount="indefinite"
              />
              <animateTransform
                attributeName="transform"
                type="rotate"
                values="-1 50 38;1.5 50 38;-1 50 38"
                dur="0.6s"
                repeatCount="indefinite"
                additive="sum"
              />
              {/* Lid knob */}
              <circle cx="50" cy="33" r="3" fill="#1D4ED8" />
              {/* Lid body */}
              <rect x="28" y="35" width="44" height="5" rx="2.5" fill="url(#lidGrad)" />
            </g>

            {/* Pot body — rounded and modern */}
            <path
              d="M 30 40 L 70 40 L 68 66 Q 67 74 58 74 L 42 74 Q 33 74 32 66 Z"
              fill="url(#potGrad)"
            />

            {/* Pot rim accent */}
            <rect x="28" y="40" width="44" height="3" rx="1.5" fill="#2563EB" opacity="0.4" />

            {/* Pot handles */}
            <path d="M 22 48 Q 18 48 18 52 Q 18 56 22 56" stroke="#1D4ED8" strokeWidth="3" strokeLinecap="round" fill="none" />
            <path d="M 78 48 Q 82 48 82 52 Q 82 56 78 56" stroke="#1D4ED8" strokeWidth="3" strokeLinecap="round" fill="none" />

            {/* Shine highlight */}
            <path
              d="M 36 46 Q 37 44 38 46 L 37 62 Q 36 64 35 62 Z"
              fill="white"
              opacity="0.15"
            />

            <defs>
              <linearGradient id="potGrad" x1="30" y1="40" x2="70" y2="74" gradientUnits="userSpaceOnUse">
                <stop offset="0%" stopColor="#3B82F6" />
                <stop offset="100%" stopColor="#1E40AF" />
              </linearGradient>
              <linearGradient id="lidGrad" x1="28" y1="35" x2="72" y2="40" gradientUnits="userSpaceOnUse">
                <stop offset="0%" stopColor="#60A5FA" />
                <stop offset="100%" stopColor="#3B82F6" />
              </linearGradient>
            </defs>
          </svg>
        </div>
      </div>

      {/* Message with fade transition */}
      <p
        className={`mt-6 ${sizes[size].text} font-medium text-slate-400 transition-opacity duration-300 ${
          fade ? 'opacity-100' : 'opacity-0'
        }`}
      >
        {displayMsg}
      </p>

      {/* Animated dots */}
      <div className="flex items-center gap-1.5 mt-3">
        {[0, 1, 2].map((i) => (
          <div
            key={i}
            className={`${sizes[size].dot} rounded-full bg-blue-400`}
            style={{
              animation: `plekoLoadDot 1.2s ease-in-out ${i * 0.2}s infinite`,
            }}
          />
        ))}
      </div>

      <style>{`
        @keyframes plekoLoadDot {
          0%, 80%, 100% { opacity: 0.3; transform: scale(1); }
          40% { opacity: 1; transform: scale(1.4); }
        }
      `}</style>
    </div>
  );
}

/**
 * Inline mini loader for inside tables and small containers.
 * Compact pot icon with pulsing animation.
 */
export function PlekoMiniLoader({ message = 'Cargando...' }: { message?: string }) {
  return (
    <div className="flex items-center justify-center gap-3 py-8">
      <div className="w-8 h-8 relative">
        <svg viewBox="0 0 56 56" fill="none" className="w-full h-full">
          {/* Steam */}
          <ellipse cx="28" cy="10" rx="3" ry="5" fill="#93C5FD" opacity="0.5">
            <animate attributeName="opacity" values="0.5;0.1;0.5" dur="1.5s" repeatCount="indefinite" />
            <animate attributeName="cy" values="10;5;10" dur="1.5s" repeatCount="indefinite" />
          </ellipse>
          {/* Pot */}
          <rect x="8" y="18" width="40" height="5" rx="2" fill="#3B82F6" />
          <path d="M 12 22 L 44 22 L 43 38 Q 42 44 36 44 L 20 44 Q 14 44 13 38 Z" fill="url(#miniPotGrad)" />
          <defs>
            <linearGradient id="miniPotGrad" x1="12" y1="22" x2="44" y2="44" gradientUnits="userSpaceOnUse">
              <stop offset="0%" stopColor="#3B82F6" />
              <stop offset="100%" stopColor="#1E40AF" />
            </linearGradient>
          </defs>
        </svg>
      </div>
      <span className="text-sm text-slate-400 font-medium">{message}</span>
    </div>
  );
}
